import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Download, Trash2, ChevronLeft, ChevronRight, MessageSquare, StickyNote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', className: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  { value: 'in_progress', label: 'In Progress', className: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { value: 'responded', label: 'Responded', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { value: 'closed', label: 'Closed', className: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <span className={`px-2 py-1 rounded-md text-xs font-medium border ${opt.className}`}>{opt.label}</span>;
}

export default function ContactsManager() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/contacts`, { params, withCredentials: true });
      setContacts(data.contacts);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/contacts/${id}/status`, { status }, { withCredentials: true });
    fetchContacts();
    if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
  };

  const addNote = async (id) => {
    if (!noteText.trim()) return;
    const { data } = await axios.post(`${API}/admin/contacts/${id}/notes`, { note: noteText }, { withCredentials: true });
    setNoteText('');
    setSelected(prev => ({ ...prev, notes: [...(prev.notes || []), data] }));
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
    if (selected?.id === id) setSelected(null);
    fetchContacts();
  };

  const exportCSV = () => {
    window.open(`${API}/admin/contacts/export`, '_blank');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchContacts();
  };

  return (
    <div className="p-8 flex gap-6" data-testid="contacts-manager">
      {/* Contact List */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white" data-testid="contacts-title">Contact Submissions</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">{total} total</span>
            <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 transition-colors" data-testid="export-btn">
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-5">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="search-input"
            />
          </form>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-slate-700 rounded-lg text-white text-sm px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400">
                <th className="text-left py-3 px-4 font-medium">Name</th>
                <th className="text-left py-3 px-4 font-medium">Email</th>
                <th className="text-left py-3 px-4 font-medium">Company</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">Loading...</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">No submissions found</td></tr>
              ) : contacts.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className={`border-b border-slate-700/50 cursor-pointer transition-colors ${selected?.id === c.id ? 'bg-blue-500/10' : 'hover:bg-slate-700/30'}`}
                  data-testid={`contact-row-${c.id}`}
                >
                  <td className="py-3 px-4 text-white font-medium">{c.name}</td>
                  <td className="py-3 px-4 text-slate-300">{c.email}</td>
                  <td className="py-3 px-4 text-slate-300">{c.company || '-'}</td>
                  <td className="py-3 px-4"><StatusBadge status={c.status || 'new'} /></td>
                  <td className="py-3 px-4 text-slate-400">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '-'}</td>
                  <td className="py-3 px-4">
                    <button onClick={(e) => { e.stopPropagation(); deleteContact(c.id); }} className="text-slate-500 hover:text-red-400 transition-colors" data-testid={`delete-contact-${c.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-400">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="w-96 bg-slate-800 rounded-xl border border-slate-700 p-6 h-fit sticky top-8" data-testid="contact-detail">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{selected.name}</h2>
            <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white text-sm">Close</button>
          </div>

          <div className="space-y-3 mb-6 text-sm">
            <div><span className="text-slate-400">Email:</span> <span className="text-white ml-2">{selected.email}</span></div>
            <div><span className="text-slate-400">Company:</span> <span className="text-white ml-2">{selected.company || '-'}</span></div>
            <div><span className="text-slate-400">Date:</span> <span className="text-white ml-2">{selected.timestamp ? new Date(selected.timestamp).toLocaleString() : '-'}</span></div>
          </div>

          <div className="mb-6">
            <label className="text-sm text-slate-400 mb-2 block">Status</label>
            <select
              value={selected.status || 'new'}
              onChange={(e) => updateStatus(selected.id, e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500"
              data-testid="detail-status-select"
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={14} className="text-slate-400" />
              <span className="text-sm text-slate-400">Message</span>
            </div>
            <p className="text-white text-sm bg-slate-700/50 rounded-lg p-3 leading-relaxed">{selected.message}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <StickyNote size={14} className="text-slate-400" />
              <span className="text-sm text-slate-400">Notes ({(selected.notes || []).length})</span>
            </div>
            {(selected.notes || []).map(n => (
              <div key={n.id} className="mb-2 p-2 bg-slate-700/30 rounded-lg text-xs">
                <p className="text-slate-300">{n.text}</p>
                <p className="text-slate-500 mt-1">{n.author} - {new Date(n.created_at).toLocaleString()}</p>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-3 py-2 placeholder-slate-500"
                data-testid="note-input"
                onKeyDown={(e) => e.key === 'Enter' && addNote(selected.id)}
              />
              <button onClick={() => addNote(selected.id)} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-note-btn">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
